import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Button,
  IconButton,
  Stack,
  TextField,
  InputAdornment,
  CircularProgress,
  useTheme,
  TablePagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Pending as PendingIcon,
  Person as PersonIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import {
  fetchRegularizations,
  approveRegularizationRequest,
  rejectRegularizationRequest,
} from "store/features/company/attendanceManagement/regularization/regularizationActions";
import {
  selectRegularizations,
  selectRegularizationsLoading,
  selectRegularizationsActionLoading,
  selectRegularizationsPagination,
} from "store/features/company/attendanceManagement/regularization/regularizationSelectors";

import usePermissions from "hooks/usePermissions";
import regularizationSvg from "assets/folders/leaves.svg";

const Regularization = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDark = theme.palette.mode === "dark";

  const regularizations = useSelector(selectRegularizations);
  const loading = useSelector(selectRegularizationsLoading);
  const actionLoading = useSelector(selectRegularizationsActionLoading);
  const pagination = useSelector(selectRegularizationsPagination);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [requestToReject, setRequestToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionError, setRejectionError] = useState("");

  const { hasPermission } = usePermissions();
  const canApprove =
    hasPermission("attendance.approve") || hasPermission("attandance.approve");

  useEffect(() => {
    const params = {
      page: page + 1,
      limit: rowsPerPage,
    };
    if (statusFilter !== "all") {
      params.status = statusFilter;
    }
    if (searchTerm) {
      params.search = searchTerm;
    }
    dispatch(fetchRegularizations(params));
  }, [dispatch, page, rowsPerPage, statusFilter, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApprove = async (id) => {
    const params = {
      page: page + 1,
      limit: rowsPerPage,
    };
    if (statusFilter !== "all") params.status = statusFilter;
    if (searchTerm) params.search = searchTerm;

    await dispatch(approveRegularizationRequest(id, params));
  };

  const handleRejectClick = (request) => {
    setRequestToReject(request);
    setRejectionReason("");
    setRejectionError("");
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      setRejectionError("Please provide a rejection reason");
      return;
    }

    const params = {
      page: page + 1,
      limit: rowsPerPage,
    };
    if (statusFilter !== "all") params.status = statusFilter;
    if (searchTerm) params.search = searchTerm;

    await dispatch(
      rejectRegularizationRequest(
        requestToReject.id,
        rejectionReason.trim(),
        params,
      ),
    );
    setRejectDialogOpen(false);
    setRequestToReject(null);
    setRejectionReason("");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: {
        label: "Pending",
        bg: "#fef3c7",
        color: "#92400e",
      },
      approved: {
        label: "Approved",
        bg: "#d1fae5",
        color: "#065f46",
      },
      rejected: {
        label: "Rejected",
        bg: "#fee2e2",
        color: "#991b1b",
      },
    };
    const config = statusMap[status] || statusMap.pending;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.bg,
          color: config.color,
          fontWeight: 600,
          fontSize: "0.75rem",
          height: "26px",
        }}
      />
    );
  };

  if (!hasPermission("attendance_records.view")) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={regularizationSvg}
              alt="Regularization"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                Attendance Regularization
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: "text.secondary",
                }}
              >
                Manage employee attendance regularization requests
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      {/* Filters */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        sx={{ mb: 3 }}
      >
        <TextField
          placeholder="Search by employee name or code..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 350 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor: isDark ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" spacing={1}>
          {["all", "pending", "approved", "rejected"].map((filter) => (
            <Button
              key={filter}
              variant={statusFilter === filter ? "contained" : "outlined"}
              onClick={() => {
                setStatusFilter(filter);
                setPage(0);
              }}
              size="small"
              sx={{
                textTransform: "capitalize",
                borderRadius: "20px",
                px: 2,
                minWidth: 70,
                ...(statusFilter === filter && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
              }}
            >
              {filter === "all" ? "All" : filter}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* Table */}
      {loading ? (
        <Box
          sx={{
            height: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress sx={{ color: "primary.main" }} />
            <Typography sx={{ color: "text.secondary" }}>
              Loading regularization requests...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            border: `1px solid ${isDark ? "#1e293b" : "#f3f4f6"}`,
            boxShadow: isDark
              ? "0 10px 25px -5px rgba(0,0,0,0.3)"
              : "0 10px 25px -5px rgba(0,0,0,0.05)",
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1000 }}>
              <TableHead
                sx={{
                  backgroundColor: isDark ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                    }}
                  >
                    Employee
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                    }}
                  >
                    Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                    }}
                  >
                    Requested Time
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      minWidth: "90px",
                    }}
                  >
                    Reason
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                    }}
                  >
                    Requested At
                  </TableCell>
                  {canApprove && (
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        textTransform: "uppercase",
                        fontSize: "0.75rem",
                      }}
                    >
                      Actions
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      minWidth: "130px",
                    }}
                  >
                    Rejection Reason
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {regularizations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <PendingIcon
                        sx={{ fontSize: 50, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography>No regularization requests found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  regularizations.map((request, index) => (
                    <TableRow
                      key={request.id}
                      component={motion.tr}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      sx={{
                        "&:hover": {
                          backgroundColor: isDark
                            ? "rgba(30,41,59,0.5)"
                            : "#f9fafb",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <PersonIcon sx={{ fontSize: 28, color: "#f59e0b" }} />
                          <Box>
                            <Typography
                              sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                            >
                              {request.employee_name ||
                                `${request.first_name} ${request.last_name}`}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: isDark ? "#7086a3" : "#465872",
                              }}
                            >
                              {request.employee_code}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: isDark ? "#7086a3" : "#465872",
                              }}
                            >
                              {request.designation_name} •{" "}
                              {request.department_name}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 500 }}>
                          {formatDate(request.attendance_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <LoginIcon
                              sx={{ fontSize: 16, color: "#10b981" }}
                            />
                            <span style={{ fontWeight: 600, color: "#10b981" }}>
                              In:
                            </span>
                            <span
                              style={{ color: isDark ? "#7086a3" : "#465872" }}
                            >
                              {request.requested_check_in || "-"}
                            </span>
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <LogoutIcon
                              sx={{ fontSize: 16, color: "#ef4444" }}
                            />
                            <span style={{ fontWeight: 600, color: "#ef4444" }}>
                              Out:
                            </span>
                            <span
                              style={{ color: isDark ? "#7086a3" : "#465872" }}
                            >
                              {request.requested_check_out || "-"}
                            </span>
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            fontStyle: "italic",
                            color: isDark ? "#f59e0b" : "#f79f07",
                            fontWeight: 500,
                          }}
                        >
                          "{request.reason || "-"}"
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(request.status)}
                        {request.status === "approved" && request.approved_by_name && (
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: isDark ? "#94a3b8" : "#64748b",
                              mt: 0.5,
                              fontWeight: 500,
                            }}
                          >
                            By: {request.approved_by_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: isDark ? "#7086a3" : "#465872",
                          }}
                        >
                          {formatDateTime(request.created_at)}
                        </Typography>
                      </TableCell>
                      {canApprove && (
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                          >
                            {request.status === "pending" ? (
                              <>
                                <Tooltip title="Approve">
                                  <IconButton
                                    onClick={() => handleApprove(request.id)}
                                    disabled={actionLoading}
                                    size="small"
                                    sx={{
                                      color: "#10b981",
                                      "&:hover": { backgroundColor: "#d1fae5" },
                                    }}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject">
                                  <IconButton
                                    onClick={() => handleRejectClick(request)}
                                    disabled={actionLoading}
                                    size="small"
                                    sx={{
                                      color: "#ef4444",
                                      "&:hover": { backgroundColor: "#fee2e2" },
                                    }}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : request.status === "approved" ? (
                              <Tooltip title="Approved">
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <IconButton
                                    disabled
                                    size="small"
                                    sx={{
                                      color: "#9ca3af",
                                      backgroundColor: "transparent",
                                      "&.Mui-disabled": {
                                        color: "#9ca3af",
                                        backgroundColor: "transparent",
                                      },
                                    }}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Tooltip>
                            ) : request.status === "rejected" ? (
                              <Tooltip title="Rejected">
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <IconButton
                                    disabled
                                    size="small"
                                    sx={{
                                      color: "#9ca3af",
                                      backgroundColor: "transparent",
                                      "&.Mui-disabled": {
                                        color: "#9ca3af",
                                        backgroundColor: "transparent",
                                      },
                                    }}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Tooltip>
                            ) : null}
                          </Stack>
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            fontStyle: "italic",
                            color: isDark ? "rgb(250, 42, 42)" : "#f50b0b",
                            fontWeight: 500,
                            textAlign: "center",
                          }}
                        >
                          {request.rejection_reason || "-"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={pagination?.total_records || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setRequestToReject(null);
          setRejectionReason("");
          setRejectionError("");
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: isDark ? "#0f172a" : "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Reject Regularization Request
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to reject the regularization request for{" "}
            <strong>{requestToReject?.employee_name}</strong>?
          </DialogContentText>
          <TextField
            label="Rejection Reason"
            multiline
            rows={3}
            fullWidth
            value={rejectionReason}
            onChange={(e) => {
              setRejectionReason(e.target.value);
              if (rejectionError) setRejectionError("");
            }}
            error={!!rejectionError}
            helperText={rejectionError}
            placeholder="Please provide a reason for rejection..."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setRequestToReject(null);
              setRejectionReason("");
              setRejectionError("");
            }}
            disabled={actionLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            disabled={actionLoading}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
              },
            }}
          >
            {actionLoading ? (
              <CircularProgress size={20} sx={{ color: "#ffffff" }} />
            ) : (
              "Reject"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Regularization;
